import { Service, signal } from '@angular/core';

const STORAGE_KEY = 'jtms.dismissedPageNotices';

/**
 * Which page notices the user has closed, kept in localStorage so it's per browser. Notice ids
 * carry a version (e.g. `inventory-review.v1`); bump it when the wording changes to show it again.
 */
@Service()
export class PageNoticeService {
  readonly #dismissed = signal<ReadonlySet<string>>(readDismissed());

  // Where focus goes after the next render, so closing or reopening a notice doesn't drop it.
  readonly focusTarget = signal<{ id: string; target: 'notice' | 'toggle' } | null>(null);

  isDismissed(id: string): boolean {
    return this.#dismissed().has(id);
  }

  dismiss(id: string): void {
    this.#update(id, true);
    this.focusTarget.set({ id, target: 'toggle' });
  }

  restore(id: string): void {
    this.#update(id, false);
    this.focusTarget.set({ id, target: 'notice' });
  }

  #update(id: string, dismissed: boolean): void {
    this.#dismissed.update((current) => {
      const next = new Set(current);
      if (dismissed) next.add(id);
      else next.delete(id);
      return next;
    });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.#dismissed()]));
    } catch {
      // Storage blocked or full: the notice stays closed for this session only.
    }
  }
}

function readDismissed(): ReadonlySet<string> {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return new Set(Array.isArray(stored) ? stored.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}
